import type { SxProps, Theme } from "@mui/material";
import { Box, Button, IconButton, lighten, useMediaQuery, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DateRange, DayEventHandler, Matcher } from "react-day-picker";
import { DayPicker } from "react-day-picker";
import DayTooltip from "@/components/DayTooltip";
import "react-day-picker/dist/style.css";
import { useLingui } from "@lingui/react";
import { Plural, Trans } from "@lingui/react/macro";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import {
  addDays,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  format,
  isSameDay,
  type Locale,
  startOfWeek,
} from "date-fns";
import { getDateFnsLocale } from "@/utils/booking";

const HOVER_CLASS_NAMES = {
  hoverRangeStart: "hoverRangeStart",
  hoverRangeMiddle: "hoverRangeMiddle",
  hoverRangeEnd: "hoverRangeEnd",
};

function WeekdayHeader({ locale }: { locale: Locale }) {
  const theme = useTheme();
  const weekStart = startOfWeek(new Date(2024, 0, 1), { weekStartsOn: 1 });
  const labels = Array.from({ length: 7 }, (_, i) =>
    format(addDays(weekStart, i), "cccccc", { locale }),
  );
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        flexShrink: 0,
        borderBottom: `1px solid ${theme.palette.divider}`,
        fontSize: theme.typography.caption.fontSize,
        fontWeight: "normal",
        color: "text.secondary",
        textAlign: "center",
        py: "0.5rem",
      }}
    >
      {labels.map((label) => (
        <Box key={label} component="span">
          {label}
        </Box>
      ))}
    </Box>
  );
}

interface DateRangeCalendarProps {
  dateRange: DateRange | undefined;
  onSelect: (range: DateRange | undefined) => void;
  defaultMonth?: Date;
  disabled?: Matcher | Matcher[];
  modifiers?: Record<string, Matcher | Matcher[]>;
  modifiersStyles?: Record<string, React.CSSProperties>;
  modifiersClassNames?: Record<string, string>;
  onDayMouseEnter?: DayEventHandler<React.MouseEvent>;
  onDayMouseLeave?: DayEventHandler<React.MouseEvent>;
  sx?: SxProps<Theme>;
  getDayTooltip?: (date: Date) => string | null;
}

export default function DateRangeCalendar({
  dateRange,
  onSelect,
  defaultMonth,
  disabled,
  modifiers,
  modifiersStyles,
  modifiersClassNames,
  onDayMouseEnter,
  onDayMouseLeave,
  sx: sxProp,
  getDayTooltip,
}: DateRangeCalendarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const { i18n } = useLingui();
  const locale = getDateFnsLocale(i18n.locale);

  const calendarRef = useRef<HTMLDivElement>(null);

  // scroll to selected month on mount on mobile
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only effect
  useEffect(() => {
    if (!isMobile || !calendarRef.current || !defaultMonth) return;

    const months = calendarRef.current.querySelectorAll(".rdp-month");
    const monthIndex = differenceInCalendarMonths(defaultMonth, new Date());
    if (monthIndex > 0 && monthIndex < months.length) {
      months[monthIndex]?.scrollIntoView({ behavior: "instant", block: "nearest" });
    }
  }, []);

  const MOBILE_MONTHS_STEP = 3;
  const [mobileMonths, setMobileMonths] = useState(() => {
    if (!defaultMonth) return MOBILE_MONTHS_STEP;
    const diff = differenceInCalendarMonths(defaultMonth, new Date());
    return Math.max(MOBILE_MONTHS_STEP, diff + 2);
  });

  const [hoveredDay, setHoveredDay] = useState<Date | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [tooltipText, setTooltipText] = useState<string | null>(null);
  const [tooltipAnchorEl, setTooltipAnchorEl] = useState<HTMLElement | null>(null);

  const isSelecting =
    !!dateRange?.from && (!dateRange?.to || isSameDay(dateRange.from, dateRange.to));

  const nightCount = useMemo(() => {
    if (!isSelecting || !hoveredDay || !dateRange?.from) return 0;
    const nights = differenceInCalendarDays(hoveredDay, dateRange.from);
    return nights > 0 ? nights : 0;
  }, [isSelecting, hoveredDay, dateRange?.from]);

  const hoverMiddleRange = useMemo(() => {
    if (nightCount <= 1 || !dateRange?.from || !hoveredDay) return [];
    return { from: addDays(dateRange.from, 1), to: addDays(hoveredDay, -1) };
  }, [nightCount, dateRange, hoveredDay]);

  const handleDayMouseEnter: DayEventHandler<React.MouseEvent> = useCallback(
    (day, mods, e) => {
      const el = e.currentTarget as HTMLElement;
      setHoveredDay(day);
      setAnchorEl(el);
      if (getDayTooltip && nightCount === 0) {
        const text = getDayTooltip(day);
        if (text) {
          setTooltipText(text);
          setTooltipAnchorEl(el);
        }
      }
      onDayMouseEnter?.(day, mods, e);
    },
    [onDayMouseEnter, getDayTooltip, nightCount],
  );

  const handleDayMouseLeave: DayEventHandler<React.MouseEvent> = useCallback(
    (day, mods, e) => {
      setHoveredDay(null);
      setAnchorEl(null);
      setTooltipText(null);
      setTooltipAnchorEl(null);
      onDayMouseLeave?.(day, mods, e);
    },
    [onDayMouseLeave],
  );

  const handleDayClick = (day: Date) => {
    setHoveredDay(null);
    setAnchorEl(null);
    // reset the current selection when a new day is clicked
    if (dateRange?.from && dateRange?.to && dateRange.from !== dateRange.to) {
      onSelect({ from: day, to: day });
    }
  };

  const lightPrimaryColor = lighten(theme.palette.primary.main, 0.85);

  const styles: SxProps<Theme> = {
    fontSize: theme.typography.body2.fontSize,
    fontWeight: 600,
    height: "100%",
    ...(isMobile && {
      display: "flex",
      flexDirection: "column",
    }),
    "& .rdp-day": {
      padding: 0,
    },
    "& .rdp-outside": {
      color: theme.palette.text.secondary,
    },
    "& .rdp-range_start, & .hoverRangeStart": {
      background: "none",
      backgroundImage: `linear-gradient(to bottom, ${lightPrimaryColor}, ${lightPrimaryColor})`,
      backgroundSize: "100% 40px",
      backgroundPosition: "21px 50%",
      backgroundRepeat: "no-repeat",
    },
    "& .rdp-range_end, & .hoverRangeEnd": {
      background: "none",
      backgroundImage: `linear-gradient(to bottom, ${lightPrimaryColor}, ${lightPrimaryColor})`,
      backgroundSize: "50% 40px",
      backgroundPosition: "0 50%",
      backgroundRepeat: "no-repeat",
    },
    "& .rdp-range_start.hoverRangeStart.rdp-range_end": {
      background: "none",
      backgroundImage: `linear-gradient(to bottom, ${lightPrimaryColor}, ${lightPrimaryColor})`,
      backgroundSize: "100% 40px",
      backgroundPosition: "21px 50%",
      backgroundRepeat: "no-repeat",
    },
    "& .rdp-range_start.rdp-range_end": {
      background: "none",
    },
    "& .rdp-day_button": {
      width: "40px",
      height: "40px",
      margin: "0 auto",
    },
    "& .rdp-selected": {
      fontSize: theme.typography.caption.fontSize,
    },
    "& .rdp-range_start .rdp-day_button, & .hoverRangeStart .rdp-day_button": {
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderColor: theme.palette.primary.main,
    },
    "& .rdp-range_end .rdp-day_button, & .hoverRangeEnd .rdp-day_button": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderColor: theme.palette.primary.main,
    },
    "& .rdp-month_caption": {
      fontSize: theme.typography.body1.fontSize,
      height: 55,
      fontWeight: "normal",
      ...(isMobile && {
        position: "sticky",
        top: 0,
        zIndex: 1,
        height: "auto",
        p: 1,
        marginInline: "0px !important",
      }),
    },
    "& .rdp-range_middle .rdp-day_button, & .hoverRangeMiddle .rdp-day_button": {
      backgroundColor: lightPrimaryColor,
      width: "100%",
      borderRadius: "0%",
    },
    "& .rdp-today": {
      color: theme.palette.text.primary,
      fontWeight: 600,
    },
    "& .rdp-weekdays": {
      boxShadow: `inset 0 1px 0 0 ${theme.palette.divider}, inset 0 -1px 0 0 ${theme.palette.divider}`,
      ...(isMobile && {
        display: "none",
      }),
    },
    "& .rdp-weekday": {
      opacity: 1,
      fontSize: theme.typography.caption.fontSize,
      fontWeight: "normal",
      color: theme.palette.text.secondary,
    },
    "& .rdp-month": {
      flex: 1,
    },
    "& .rdp-button_previous, & .rdp-button_next": {
      margin: "0 10px",
      top: "5px !important",
    },
    "& .rdp-months": {
      gap: 0,
      maxWidth: "100%",
      height: "100%",
      position: "relative",
      ...(isMobile
        ? {
            flexDirection: "column",
          }
        : {
            "&::after": {
              content: '""',
              position: "absolute",
              top: 55, // height of month caption
              bottom: 0,
              left: "50%",
              width: "1px",
              backgroundColor: theme.palette.divider,
            },
          }),
    },
    "& .rdp-month_grid": {
      width: "100%",
    },
    "& .rdp-disabled": {
      fontWeight: "normal",
      color: lighten(theme.palette.text.disabled, 0.3),
      opacity: 1,
    },
    "& .rdp-range_middle": {
      background: "none",
      borderRadius: "0%",
    },
  };

  return (
    <Box
      ref={calendarRef}
      sx={[styles, ...(Array.isArray(sxProp) ? sxProp : [sxProp])] as SxProps<Theme>}
    >
      {isMobile && <WeekdayHeader locale={locale} />}
      <Box sx={isMobile ? { flex: 1, overflowY: "auto", minHeight: 0 } : { height: "100%" }}>
        <DayPicker
          mode="range"
          selected={dateRange}
          onSelect={onSelect}
          navLayout="around"
          numberOfMonths={isMobile ? mobileMonths : 2}
          hideNavigation={isMobile}
          weekStartsOn={1}
          disabled={disabled ?? { before: new Date() }}
          defaultMonth={isMobile ? new Date() : defaultMonth}
          locale={locale}
          modifiers={{
            ...modifiers,
            ...(nightCount > 0 && {
              hoverRangeStart: dateRange?.from ? [dateRange.from] : undefined,
              hoverRangeMiddle: hoverMiddleRange,
              hoverRangeEnd: [hoveredDay!],
            }),
          }}
          modifiersStyles={modifiersStyles}
          modifiersClassNames={{ ...HOVER_CLASS_NAMES, ...modifiersClassNames }}
          onDayMouseEnter={handleDayMouseEnter}
          onDayMouseLeave={handleDayMouseLeave}
          onDayClick={handleDayClick}
          components={{
            Chevron: ({ orientation }) => {
              return (
                <IconButton component="div">
                  {orientation === "left" ? <ChevronLeft /> : <ChevronRight />}
                </IconButton>
              );
            },
          }}
        />
        {isMobile && (
          <Box sx={{ p: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setMobileMonths((n) => n + MOBILE_MONTHS_STEP)}
              sx={{ color: "text.primary", borderColor: "divider" }}
            >
              <Trans>Load more dates</Trans>
            </Button>
          </Box>
        )}
      </Box>
      {!isMobile && (
        <DayTooltip open={nightCount > 0 && anchorEl !== null} anchorEl={anchorEl}>
          <Plural value={nightCount} one="# night" other="# nights" />
        </DayTooltip>
      )}
      {!isMobile && (
        <DayTooltip
          open={tooltipText !== null && tooltipAnchorEl !== null}
          anchorEl={tooltipAnchorEl}
        >
          {tooltipText}
        </DayTooltip>
      )}
    </Box>
  );
}
